import * as React from "react";
import { createPortal } from "react-dom";
import { Button } from "@/shared/ui/Button";

import { cn } from "@/shared/utils/shadcn";
import { Row } from "@/shared/ui/Blocks";

// Minimum gap kept between a dropdown menu and the viewport edge.
const VIEWPORT_PADDING = 8;

// DropdownMenu
export interface DropdownMenuProps {
  children: React.ReactNode;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

const DropdownMenuContext = React.createContext<{
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}>({ isOpen: false, onOpenChange: () => {} });

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  isOpen: externalIsOpen,
  onOpenChange,
}) => {
  const [internalIsOpen, setInternalIsOpen] = React.useState(false);
  const triggerRef = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;

  const handleOpenChange = (newIsOpen: boolean) => {
    if (!isControlled) {
      setInternalIsOpen(newIsOpen);
    }
    onOpenChange?.(newIsOpen);
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isOpen &&
        triggerRef.current &&
        menuRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        handleOpenChange(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <DropdownMenuContext.Provider
      value={{ isOpen, onOpenChange: handleOpenChange }}
    >
      <div className="relative inline-block text-left w-full" ref={triggerRef}>
        {React.Children.map(children, (child) => {
          if (React.isValidElement(child)) {
            if (child.type === DropdownMenuTrigger) {
              return React.cloneElement(child, {
                onClick: () => handleOpenChange(!isOpen),
              } as any);
            }
            if (child.type === DropdownMenuContent) {
              return React.cloneElement(child, {
                isOpen,
                triggerRef,
                menuRef,
              } as any);
            }
          }
          return child;
        })}
      </div>
    </DropdownMenuContext.Provider>
  );
};

// DropdownMenuTrigger
interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  onClick?: () => void;
  asChild?: boolean;
  manualTrigger?: boolean; // If true, trigger is controlled manually
}

export const DropdownMenuTrigger: React.FC<DropdownMenuTriggerProps> = ({
  children,
  onClick,
  asChild = false,
  manualTrigger = false,
}) => {
  if (asChild) {
    return React.cloneElement(
      React.Children.only(children) as React.ReactElement,
      {
        onClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          if (manualTrigger) return;
          (children as React.ReactElement<any>).props.onClick?.(e);
          onClick?.();
        },
      } as any
    );
  }

  return (
    <Button variant="outline" onClick={onClick}>
      {children}
    </Button>
  );
};

// DropdownMenuContent
export interface DropdownMenuContentProps {
  children: React.ReactNode;
  isOpen?: boolean;
  triggerRef?: React.RefObject<HTMLDivElement>;
  menuRef?: React.RefObject<HTMLDivElement>;
  align?: "start" | "end" | "center";
  side?: "top" | "right" | "bottom" | "left";
  sideOffset?: number;
  alignOffset?: number;
  className?: string;
  matchTriggerWidth?: boolean;
}

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  children,
  isOpen,
  triggerRef,
  menuRef,
  align = "end",
  side = "bottom",
  sideOffset = 0,
  alignOffset = 0,
  className,
  matchTriggerWidth = false,
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [position, setPosition] = React.useState<{ top: number; left: number }>(
    { top: -9999, left: -9999 }
  );
  const [width, setWidth] = React.useState<number | undefined>(undefined);

  const calculatePosition = React.useEffectEvent(() => {
    if (!triggerRef?.current || !menuRef?.current) return null;

    const triggerRect = triggerRef.current.getBoundingClientRect();
    const contentRect = menuRef.current.getBoundingClientRect();
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // The menu is `position: fixed`, so every coordinate below is
    // viewport-relative. Do NOT add window.scrollX / scrollY here.

    // Flip to the opposite side when the requested side has no room for the
    // content but the opposite side has more.
    let resolvedSide = side;
    if (side === "top" || side === "bottom") {
      const spaceBelow =
        viewportHeight - triggerRect.bottom - sideOffset - VIEWPORT_PADDING;
      const spaceAbove = triggerRect.top - sideOffset - VIEWPORT_PADDING;
      if (
        side === "bottom" &&
        contentRect.height > spaceBelow &&
        spaceAbove > spaceBelow
      ) {
        resolvedSide = "top";
      } else if (
        side === "top" &&
        contentRect.height > spaceAbove &&
        spaceBelow > spaceAbove
      ) {
        resolvedSide = "bottom";
      }
    } else {
      const spaceRight =
        viewportWidth - triggerRect.right - sideOffset - VIEWPORT_PADDING;
      const spaceLeft = triggerRect.left - sideOffset - VIEWPORT_PADDING;
      if (
        side === "right" &&
        contentRect.width > spaceRight &&
        spaceLeft > spaceRight
      ) {
        resolvedSide = "left";
      } else if (
        side === "left" &&
        contentRect.width > spaceLeft &&
        spaceRight > spaceLeft
      ) {
        resolvedSide = "right";
      }
    }

    let top = triggerRect.top;
    let left = triggerRect.left;

    // Calculate position based on side
    switch (resolvedSide) {
      case "top":
        top = triggerRect.top - contentRect.height - sideOffset;
        break;
      case "bottom":
        top = triggerRect.bottom + sideOffset;
        break;
      case "left":
        left = triggerRect.left - contentRect.width - sideOffset;
        break;
      case "right":
        left = triggerRect.right + sideOffset;
        break;
    }

    // Calculate alignment
    if (resolvedSide === "left" || resolvedSide === "right") {
      switch (align) {
        case "start":
          top = triggerRect.top + alignOffset;
          break;
        case "center":
          top =
            triggerRect.top +
            (triggerRect.height - contentRect.height) / 2 +
            alignOffset;
          break;
        case "end":
          top = triggerRect.bottom - contentRect.height + alignOffset;
          break;
      }
    } else {
      switch (align) {
        case "start":
          left = triggerRect.left + alignOffset;
          break;
        case "center":
          left =
            triggerRect.left +
            (triggerRect.width - contentRect.width) / 2 +
            alignOffset;
          break;
        case "end":
          left = triggerRect.right - contentRect.width + alignOffset;
          break;
      }
    }

    // Last resort: shift the menu so it stays inside the viewport on both axes.
    const maxLeft = viewportWidth - VIEWPORT_PADDING - contentRect.width;
    const maxTop = viewportHeight - VIEWPORT_PADDING - contentRect.height;
    left = Math.max(VIEWPORT_PADDING, Math.min(left, maxLeft));
    top = Math.max(VIEWPORT_PADDING, Math.min(top, maxTop));

    if (matchTriggerWidth) {
      setWidth(triggerRect.width);
    }

    return { top, left };
  });

  React.useEffect(() => {
    if (isOpen) {
      // Double requestAnimationFrame ensures DOM is fully rendered and styled
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const newPosition = calculatePosition();
          if (newPosition) {
            setPosition(newPosition);
            setMounted(true);
          }
        });
      });

      const handleReposition = () => {
        const newPosition = calculatePosition();
        if (newPosition) {
          setPosition(newPosition);
        }
      };

      window.addEventListener("resize", handleReposition);
      // Capture phase so scrolling inside any ancestor container (e.g. a
      // modal body) repositions the fixed menu alongside its trigger.
      window.addEventListener("scroll", handleReposition, true);
      return () => {
        window.removeEventListener("resize", handleReposition);
        window.removeEventListener("scroll", handleReposition, true);
      };
    }
    setMounted(false);
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={menuRef}
      className={cn(
        "fixed z-50 w-56 p-2 bg-white rounded-md shadow-xl focus:outline-none opacity-0 transition-opacity duration-150 ring ring-gray-100",
        mounted && "opacity-100",
        className
      )}
      style={{
        top: `${position.top}px`,
        left: `${position.left}px`,
        width: matchTriggerWidth ? `${width}px` : "auto",
        visibility: mounted ? "visible" : "hidden",
      }}
    >
      <div className="py-1">{children}</div>
    </div>,
    document.body
  );
};

// DropdownMenuItem
interface DropdownMenuItemProps {
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  className?: string;
}

export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  onClick,
  className,
}) => {
  const { onOpenChange } = React.useContext(DropdownMenuContext);

  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onClick?.(e);
    onOpenChange(false);
  };

  return (
    <Row
      className={cn(
        "w-full cursor-pointer justify-start shadow-none text-sm text-gray-700 rounded-md hover:bg-gray-100 hover:text-gray-900",
        className
      )}
      onClick={handleClick}
    >
      {children}
    </Row>
  );
};

// DropdownMenuSeparator
export const DropdownMenuSeparator: React.FC = () => (
  <hr className="my-1 border-gray-200" />
);
